import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../../../Utility/Button';
import Input from '../../../../Utility/FormElements/Input';
import toast from 'react-hot-toast';
import { mcpService } from '../../../../../Services/mcpService';
import LoadingSpinner from '../../../../Utility/LoadingSpinner';
import { RootState, useAppSelector } from '../../../../../store';
import { Badge } from '../../../../Utility/Badge';
import { InfoIcon } from '@phosphor-icons/react';

interface OAuthConfigProps {
    configId: string;
    authData: any;
    onAuthDataChange: (authData: any) => void;
    authType: string;
    hasUnsavedChanges: boolean;
}

interface OAuthStatus {
    hasToken: boolean;
    needsRefresh: boolean;
    expiresAt: number;
    expiresIn: number;
    hasRefreshToken: boolean;
}

const OAuthConfig: React.FC<OAuthConfigProps> = ({
    configId,
    authData,
    onAuthDataChange,
    authType,
    hasUnsavedChanges,
}) => {
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const [oauthStatus, setOauthStatus] = useState<OAuthStatus | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isStartingAuth, setIsStartingAuth] = useState(false);

    const isOAuth = authType === 'oauth';

    const loadOAuthStatus = useCallback(async () => {
        if (!isOAuth || !configId) return;

        try {
            const response = await mcpService.checkOAuthToken(configId);
            setOauthStatus(response.data);
        } catch (error) {
            console.error('Failed to check OAuth status:', error);
        }
    }, [isOAuth, configId]);

    useEffect(() => {
        loadOAuthStatus();

        // Check OAuth status every 30 seconds
        const interval = setInterval(loadOAuthStatus, 30000);
        return () => clearInterval(interval);
    }, [configId, authType, loadOAuthStatus]);

    const handleInputChange = (field: string, value: string) => {
        onAuthDataChange({
            ...authData,
            [field]: value,
        });
    };

    function registerDynamicallyClient() {
        mcpService.registerDynamicallyClient(configId).then((response) => {
            if (response.data.success) {
                toast.success(i18n.translate({ id: 'oauth.dcr.success', sourceName: 'MCP' }));
                onAuthDataChange(response.data.authData);
            } else {
                toast.error(response.data.message);
            }
        });
    }

    const startOAuthFlow = async () => {
        try {
            setIsStartingAuth(true);
            const response = await mcpService.startOAuth(configId);

            if (response.data.success) {
                // Open OAuth authorization URL in new window
                const authWindow = window.open(
                    response.data.authorizationUrl,
                    'oauth-popup',
                    'width=600,height=700,scrollbars=yes,resizable=yes',
                );

                // Poll for window closure (indicating OAuth completion)
                const pollTimer = setInterval(() => {
                    if (authWindow?.closed) {
                        clearInterval(pollTimer);
                        // Reload OAuth status after authorization
                        setTimeout(() => {
                            loadOAuthStatus();
                            toast.success(i18n.translate({ id: 'oauth.start.success', sourceName: 'MCP' }));
                        }, 1000);
                    }
                }, 1000);

                // Cleanup after 10 minutes
                setTimeout(() => {
                    clearInterval(pollTimer);
                    if (authWindow && !authWindow.closed) {
                        authWindow.close();
                    }
                }, 600000);
            } else {
                toast.error(response.data.message || i18n.translate({ id: 'oauth.start.failed', sourceName: 'MCP' }));
            }
        } catch (error) {
            console.error('Failed to start OAuth:', error);
            toast.error(i18n.translate({ id: 'oauth.start.error', sourceName: 'MCP' }));
        } finally {
            setIsStartingAuth(false);
        }
    };

    const refreshToken = async () => {
        try {
            setIsRefreshing(true);
            const response = await mcpService.refreshOAuthToken(configId);

            if (response.data.success) {
                toast.success(i18n.translate({ id: 'oauth.token.refresh.success', sourceName: 'MCP' }));
                loadOAuthStatus();
            } else {
                toast.error(
                    response.data.message || i18n.translate({ id: 'oauth.token.refresh.failed', sourceName: 'MCP' }),
                );
            }
        } catch (error) {
            console.error('Failed to refresh token:', error);
            toast.error(i18n.translate({ id: 'oauth.token.refresh.error', sourceName: 'MCP' }));
        } finally {
            setIsRefreshing(false);
        }
    };

    const formatExpiresAt = (timestamp: number) => {
        if (!timestamp) return i18n.translate({ id: 'oauth.status.unknown', sourceName: 'MCP' });
        return new Date(timestamp * 1000).toLocaleString('de-DE');
    };

    const formatExpiresIn = (seconds: number) => {
        if (seconds <= 0) return i18n.translate({ id: 'oauth.status.expired', sourceName: 'MCP' });

        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);

        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        }
        return `${minutes}m`;
    };

    if (!isOAuth) {
        return null;
    }

    if (!configId) {
        return (
            <Badge variant="info" className="w-full mt-2">
                <div className="flex items-start">
                    <div className="flex-shrink-0">
                        <InfoIcon size={20} />
                    </div>
                    <div className="ml-2">
                        <div className="text-sm ">{i18n.translate({ id: 'oauth.info', sourceName: 'MCP' })}</div>
                    </div>
                </div>
            </Badge>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h4 className="mb-4">{i18n.translate({ id: 'oauth.title', sourceName: 'MCP' })}</h4>
                <p className="text-sm text-layout-textcolor/70 mb-4">
                    {i18n.translate({ id: 'oauth.description.pkce', sourceName: 'MCP' })}
                </p>
            </div>

            {/* OAuth Configuration Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-layout-textcolor mb-2">
                        {i18n.translate({ id: 'oauth.authorizationUrl', sourceName: 'MCP' })} *
                    </label>
                    <Input
                        value={authData.authorizationUrl || ''}
                        onChange={(value) => handleInputChange('authorizationUrl', value)}
                        placeholder="https://provider.com/oauth/authorize"
                        required
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-layout-textcolor mb-2">
                        {i18n.translate({ id: 'oauth.tokenUrl', sourceName: 'MCP' })} *
                    </label>
                    <Input
                        value={authData.tokenUrl || ''}
                        onChange={(value) => handleInputChange('tokenUrl', value)}
                        placeholder="https://provider.com/oauth/token"
                        required
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-layout-textcolor mb-2">
                        {i18n.translate({ id: 'oauth.clientId', sourceName: 'MCP' })} *
                    </label>
                    <Input
                        value={authData.clientId || ''}
                        onChange={(value) => handleInputChange('clientId', value)}
                        placeholder="your-client-id"
                        required
                    />
                    {!authData.clientId && authData.registrationEndpoint && (
                        <span
                            onClick={registerDynamicallyClient}
                            className="underline cursor-pointer hover:text-primary transition-colors duration-100 tooltip"
                            data-tooltip-content={i18n.translate({ id: 'oauth.dcr.description', sourceName: 'MCP' })}
                        >
                            {i18n.translate({ id: 'oauth.dcr.button', sourceName: 'MCP' })}
                        </span>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-layout-textcolor mb-2">
                        {i18n.translate({ id: 'oauth.clientSecret', sourceName: 'MCP' })}
                    </label>
                    <Input
                        value={authData.clientSecret || ''}
                        onChange={(value) => handleInputChange('clientSecret', value)}
                        placeholder="your-client-secret"
                        inputType="password"
                        hasVisibilityButton={true}
                    />
                </div>

                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-layout-textcolor mb-2">
                        {i18n.translate({ id: 'oauth.scope', sourceName: 'MCP' })}
                    </label>
                    <Input
                        value={authData.scope || ''}
                        onChange={(value) => handleInputChange('scope', value)}
                        placeholder="read write"
                    />
                </div>
            </div>

            {/* OAuth Status */}
            {configId && (
                <div className="bg-layout-backgroundColor border border-layout-borderColor rounded-lg p-6">
                    <h5 className="mb-4">{i18n.translate({ id: 'oauth.status.title', sourceName: 'MCP' })}</h5>

                    {oauthStatus ? (
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span className="text-layout-textcolor/70">
                                        {i18n.translate({ id: 'oauth.status.token', sourceName: 'MCP' })}:
                                    </span>
                                    <span
                                        className={`ml-2 font-medium ${
                                            oauthStatus.hasToken
                                                ? oauthStatus.needsRefresh
                                                    ? 'text-yellow-600'
                                                    : 'text-green-600'
                                                : 'text-red-600'
                                        }`}
                                    >
                                        {oauthStatus.hasToken
                                            ? oauthStatus.needsRefresh
                                                ? i18n.translate({ id: 'oauth.status.needsRefresh', sourceName: 'MCP' })
                                                : i18n.translate({ id: 'oauth.status.active', sourceName: 'MCP' })
                                            : i18n.translate({ id: 'oauth.status.noToken', sourceName: 'MCP' })}
                                    </span>
                                </div>

                                {oauthStatus.hasToken && (
                                    <>
                                        <div>
                                            <span className="text-layout-textcolor/70">
                                                {i18n.translate({ id: 'oauth.status.expiresAt', sourceName: 'MCP' })}:
                                            </span>
                                            <span className="ml-2 font-medium text-layout-textcolor">
                                                {formatExpiresAt(oauthStatus.expiresAt)}
                                            </span>
                                        </div>

                                        <div>
                                            <span className="text-layout-textcolor/70">
                                                {i18n.translate({ id: 'oauth.status.remaining', sourceName: 'MCP' })}:
                                            </span>
                                            <span className="ml-2 font-medium text-layout-textcolor">
                                                {formatExpiresIn(oauthStatus.expiresIn)}
                                            </span>
                                        </div>

                                        <div>
                                            <span className="text-layout-textcolor/70">
                                                {i18n.translate({ id: 'oauth.status.refreshToken', sourceName: 'MCP' })}
                                                :
                                            </span>
                                            <span
                                                className={`ml-2 font-medium ${
                                                    oauthStatus.hasRefreshToken ? 'text-green-600' : 'text-red-600'
                                                }`}
                                            >
                                                {oauthStatus.hasRefreshToken
                                                    ? i18n.translate({
                                                          id: 'oauth.status.available',
                                                          sourceName: 'MCP',
                                                      })
                                                    : i18n.translate({
                                                          id: 'oauth.status.unavailable',
                                                          sourceName: 'MCP',
                                                      })}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="flex gap-3">
                                <Button
                                    onClick={startOAuthFlow}
                                    variant={
                                        isStartingAuth ||
                                        !authData.authorizationUrl ||
                                        !authData.tokenUrl ||
                                        !authData.clientId
                                            ? 'disabled'
                                            : 'primary'
                                    }
                                    className="flex-1 tooltip"
                                    tooltipText={
                                        hasUnsavedChanges
                                            ? i18n.translate({ id: 'oauth.start.tooltip', sourceName: 'MCP' })
                                            : undefined
                                    }
                                >
                                    {isStartingAuth ? (
                                        <>
                                            <LoadingSpinner className="mr-2" />
                                            {i18n.translate({ id: 'oauth.starting', sourceName: 'MCP' })}
                                        </>
                                    ) : oauthStatus.hasToken ? (
                                        i18n.translate({ id: 'oauth.renew', sourceName: 'MCP' })
                                    ) : (
                                        i18n.translate({ id: 'oauth.start', sourceName: 'MCP' })
                                    )}
                                </Button>

                                {oauthStatus.hasToken && oauthStatus.hasRefreshToken && (
                                    <Button
                                        onClick={refreshToken}
                                        disabled={isRefreshing}
                                        variant="secondary"
                                        className="flex-1"
                                    >
                                        {isRefreshing ? (
                                            <>
                                                <LoadingSpinner className="mr-2" />
                                                {i18n.translate({ id: 'oauth.refreshing', sourceName: 'MCP' })}
                                            </>
                                        ) : (
                                            i18n.translate({ id: 'oauth.refresh', sourceName: 'MCP' })
                                        )}
                                    </Button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center">
                            <LoadingSpinner className="mr-2" />
                            <span className="text-layout-textcolor/70">
                                {i18n.translate({ id: 'oauth.status.loading', sourceName: 'MCP' })}
                            </span>
                        </div>
                    )}
                </div>
            )}

            {/* OAuth Help */}
            <Badge variant="info" className="block w-full">
                <div>
                    <h5 className="font-medium mb-2">{i18n.translate({ id: 'oauth.title', sourceName: 'MCP' })}</h5>
                    <div className="text-sm">
                        <p className="mb-2">{i18n.translate({ id: 'oauth.help.requirements', sourceName: 'MCP' })}</p>
                        <ul className="list-disc pl-4 space-y-1">
                            <li>{i18n.translate({ id: 'oauth.help.authorizationUrl', sourceName: 'MCP' })}</li>
                            <li>{i18n.translate({ id: 'oauth.help.tokenUrl', sourceName: 'MCP' })}</li>
                            <li>{i18n.translate({ id: 'oauth.help.clientId', sourceName: 'MCP' })}</li>
                            <li>{i18n.translate({ id: 'oauth.help.clientSecret', sourceName: 'MCP' })}</li>
                            <li>{i18n.translate({ id: 'oauth.help.scopes', sourceName: 'MCP' })}</li>
                        </ul>
                        <p className="mt-2">
                            {i18n.translate({ id: 'oauth.help.redirectUri', sourceName: 'MCP' })}: <br />
                            <code className="bg-white bg-opacity-20 px-1 rounded">
                                {window.location.origin}/api/mcp/oauth/callback
                            </code>
                        </p>
                        {authData.serviceDocumentation && (
                            <p className="mt-2">
                                {i18n.translate({ id: 'oauth.help.serviceDocumentation', sourceName: 'MCP' })}: <br />
                                <code className="bg-white bg-opacity-20 px-1 rounded">
                                    {authData.serviceDocumentation}
                                </code>
                            </p>
                        )}
                    </div>
                </div>
            </Badge>
        </div>
    );
};

export default OAuthConfig;
